import { useRef } from "react";
import { useSelector } from "react-redux";
import { selectTableRowsPerPage } from "../../state/selectors";
import PropTypes, { bool } from "prop-types";

import { t } from "../../services";
import { useCustomCellProps } from "../../hooks";

import { SVG, Tooltip } from "../../components";
import { TableStyled, TableWrapper } from "./styled";
import TableInfo from "./TableInfo";
import TableComponent from "./TableComponent";

import TableRow from "./TableRow";
import useTableCellWidths from "./hooks/useTableCellWidths";

const Table = ({
  tableKey,
  tableInstance,
  maxTableHeight,
  searchedText,
  searchInfo,
  searchCheckInfo,
  noDataInfo,
  component,
  componentHeight,
  editId,
  editDisabled,
  editErrors,
  callbackFn1 = () => {},
  callbackFn2 = () => {},
  callbackFn3 = () => {},
  callbackFn4 = () => {},
  callbackFn5 = () => {},
  callbackFn6 = () => {},
  getTooltipMessage,
  onRowClick,
  hasPagination,
  showTableBorder,
  tooltipLabel,
  modalTable,
  modalTableSmall,
  withAdditionalElement,
  disableScroll,
  maxVisibleRows,
  isLoading,
  isValid
}) => {
  const tableRef = useRef();
  const tableWrapperRef = useRef();
  const createdTableRowsIds = useSelector((store) => store.global.createdTableRowsIds);
  const selectedTableRowsIds = useSelector((state) => state.global.selectedTableRowsIds);

  const {
    getTableProps, // table props from react-table
    getTableBodyProps, // table body props from react-table
    headerGroups, // headerGroups, if your table has groupings
    rows, // rows for the table based on the data passed
    page, // rows for the table based on the data passed (when using usePaginantion hook)
    prepareRow // Prepare the row (this function needs to be called for each row before getting the row props)
  } = tableInstance;

  const tableData = hasPagination && page ? page : rows;
  const tableRowsPerPage = useSelector(selectTableRowsPerPage(tableKey));

  const tableBodyRef = useRef(null);

  const customCellProps = useCustomCellProps({
    tableRef,
    tableWrapperRef,
    searchedText,
    editId,
    editDisabled,
    editErrors,
    isLoading,
    isValid,
    callbackFn1,
    callbackFn2,
    callbackFn3,
    callbackFn4,
    callbackFn5,
    callbackFn6
  });

  const { cellRefs, colWidths } = useTableCellWidths(modalTable, tableData);

  return (
    <TableWrapper
      ref={tableWrapperRef}
      disableScroll={disableScroll}
      maxVisibleRows={maxVisibleRows}
      tableSize={tableRowsPerPage}
      totalRows={rows?.length}
      componentHeight={componentHeight ?? 0}
      showTableBorder={showTableBorder}
      maxTableHeight={maxTableHeight}
      modalTable={modalTable}
      modalTableSmall={modalTableSmall}>
      <TableStyled
        ref={tableRef}
        {...getTableProps()}
        hasClickableRows={onRowClick != undefined}
        componentHeight={componentHeight ?? 0}
        addOutline={!component && tableData?.length}
        component={!!component}
        disableScroll={disableScroll}
        withAdditionalElement={withAdditionalElement}>
        <thead>
          {headerGroups.map((headerGroup, index) => (
            <tr {...headerGroup.getHeaderGroupProps()} key={index}>
              {headerGroup.headers.map((column, index) => (
                <th
                  key={index}
                  {...column.getHeaderProps(
                    column?.getSortByToggleProps && column.getSortByToggleProps()
                  )}
                  style={{
                    width: `${colWidths[index]}px` ?? "100%"
                  }}>
                  <div>
                    {t(column.render("Header", customCellProps))}

                    <span>
                      {column?.isSorted ? (
                        <SVG.TableSortArrow flipVert={column?.isSortedDesc} />
                      ) : (
                        ""
                      )}
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        {tableData?.length > 0 ? (
          <>
            <tbody {...getTableBodyProps()} ref={tableBodyRef}>
              {tableData.map((row, rowIndex) => {
                prepareRow(row);

                return (
                  <TableRow
                    key={rowIndex}
                    rowIndex={rowIndex}
                    row={row}
                    onRowClick={onRowClick}
                    disabled={isLoading}
                    cellRefs={cellRefs}
                    customCellProps={customCellProps}
                    isRecentlyCreated={createdTableRowsIds.includes(row.original.id)}
                    isSelected={selectedTableRowsIds.includes(row.original.id)}
                    getTooltipMessage={getTooltipMessage}
                  />
                );
              })}
            </tbody>
            {onRowClick && !getTooltipMessage && tooltipLabel && (
              <Tooltip childrenRef={tableBodyRef} tooltipLabel={tooltipLabel} />
            )}
          </>
        ) : (
          !component && (
            <TableInfo
              searchedText={searchedText}
              searchInfo={searchInfo}
              searchCheckInfo={searchCheckInfo}
              noDataInfo={noDataInfo}
              disableScroll={disableScroll}
              modalTable={modalTable}
            />
          )
        )}

        {component && (
          <TableComponent
            disableScroll={disableScroll}
            component={component}
            modalTable={modalTable}
          />
        )}
      </TableStyled>
    </TableWrapper>
  );
};

Table.propTypes = {
  tableInstance: PropTypes.shape({
    getTableProps: PropTypes.func.isRequired,
    getTableBodyProps: PropTypes.func.isRequired,
    headerGroups: PropTypes.arrayOf(
      PropTypes.shape({
        getHeaderGroupProps: PropTypes.func,
        headers: PropTypes.arrayOf(
          PropTypes.shape({
            getHeaderProps: PropTypes.func.isRequired,
            render: PropTypes.func.isRequired
          })
        ).isRequired
      })
    ).isRequired,
    rows: PropTypes.arrayOf(
      PropTypes.shape({
        getRowProps: PropTypes.func,
        cells: PropTypes.arrayOf(
          PropTypes.shape({
            getCellProps: PropTypes.func.isRequired,
            render: PropTypes.func
          })
        )
      })
    ),
    page: PropTypes.arrayOf(
      PropTypes.shape({
        getRowProps: PropTypes.func,
        cells: PropTypes.arrayOf(
          PropTypes.shape({
            getCellProps: PropTypes.func.isRequired,
            render: PropTypes.func
          })
        )
      })
    ),
    prepareRow: PropTypes.func.isRequired
  }).isRequired,
  maxTableHeight: PropTypes.string,
  searchedText: PropTypes.string,
  searchInfo: PropTypes.string,
  searchCheckInfo: PropTypes.string,
  noDataInfo: PropTypes.string,
  component: PropTypes.oneOfType([PropTypes.node, PropTypes.object]),
  componentHeight: PropTypes.number,
  editId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  editDisabled: PropTypes.bool,
  editErrors: PropTypes.arrayOf(PropTypes.string),
  callbackFn1: PropTypes.func,
  callbackFn2: PropTypes.func,
  callbackFn3: PropTypes.func,
  callbackFn4: PropTypes.func,
  callbackFn5: PropTypes.func,
  callbackFn6: PropTypes.func,
  onRowClick: PropTypes.func,
  getTooltipMessage: PropTypes.func,
  hasPagination: PropTypes.bool,
  isRowsClickable: PropTypes.bool,
  showTableBorder: PropTypes.bool,
  tooltipLabel: PropTypes.string,
  modalTable: PropTypes.bool,
  modalTableSmall: PropTypes.bool,
  withAdditionalElement: PropTypes.bool,
  disableScroll: PropTypes.bool,
  maxVisibleRows: PropTypes.number,
  isLoading: bool,
  isValid: PropTypes.bool,
  tableKey: PropTypes.string
};

export default Table;
